import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { environment } from '../../environments/environment';
import SendIcon from '@mui/icons-material/Send';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ReasoningLine, useReasoningStream } from './useReasoningStream';
import { dsVar } from '../theme/tokens';

export interface ChatAssistanceProps {
    agentCode?: string;
    title?: string;
    subtitle?: string;
    initialMessage?: string;
    contextData?: Record<string, any>;
    /**
     * Sent with every message when given, so the agent sees one conversation. Omitted,
     * each message goes out under a fresh id, as it always has.
     */
    interactionId?: string;
    placeholder?: string;
    /**
     * Show the agent's live reasoning (/ws/reasoning-logs) in place of the loading dots
     * until the answer arrives.
     */
    showReasoning?: boolean;
    /**
     * Opens an insight the answer drew on. The answer's references are listed as
     * links only when given; without it they are left out, as before.
     */
    onOpenReference?: (reference: AgentReference) => void;
    /** The reference whose insight is open now, marked in the list. */
    activeReferenceId?: string | null;
    /**
     * Draws a "New conversation" button in the header (so only with a `title`). The
     * caller starts over by passing a new interactionId and keying the panel on it,
     * which clears the thread too; useChatPanel does both.
     */
    onNewConversation?: () => void;
    /** Whether the panel is at its wide width. Read only alongside onToggleExpand. */
    expanded?: boolean;
    /** Draws a widen/narrow toggle in the header (so only with a `title`). The caller owns the width. */
    onToggleExpand?: () => void;
    /** Why the panel cannot widen now, when it cannot: the toggle is disabled and says so. */
    expandUnavailable?: string;
    /** Draws a "Hide assistant" button in the header (so only with a `title`). The caller owns visibility. */
    onHide?: () => void;
}

/** An insight an answer drew on, openable with /agent/flow/output/insight. */
export interface AgentReference {
    insightId: string;
    title: string;
    informationCategory?: string;
}

/**
 * The references in one item of an answer. They arrive inside a DICTIONARY's
 * entries rather than under a key of their own; a top-level key is read too.
 * References without an insightId cannot be opened and are dropped.
 */
const referencesOf = (item: any): AgentReference[] => {
    const raw = item?.key === 'references'
        ? item.value
        : Array.isArray(item?.entries) ? item.entries.find((e: any) => e?.key === 'references')?.value : undefined;
    if (!Array.isArray(raw)) return [];
    return raw
        .filter((ref: any) => ref && typeof ref.insightId === 'string' && ref.insightId)
        .map((ref: any) => ({
            insightId: ref.insightId,
            title: String(ref.title || ref.groupName || 'Untitled insight'),
            informationCategory: ref.informationCategory ? String(ref.informationCategory) : undefined,
        }));
};

/** How many references show before the rest fold behind "Show more". */
const REFERENCES_SHOWN = 4;

const ReferenceList: React.FC<{
    references: AgentReference[];
    activeReferenceId?: string | null;
    onOpen: (reference: AgentReference) => void;
}> = ({ references, activeReferenceId, onOpen }) => {
    const [showAll, setShowAll] = useState(false);
    const shown = showAll ? references : references.slice(0, REFERENCES_SHOWN);
    const hidden = references.length - shown.length;
    return (
        <div className="mt-3 space-y-1.5">
            <p className="text-[10px] text-fg-muted font-bold uppercase tracking-wider mb-1">References</p>
            {shown.map((ref) => {
                const active = ref.insightId === activeReferenceId;
                return (
                    <button
                        key={ref.insightId}
                        type="button"
                        onClick={() => onOpen(ref)}
                        aria-haspopup="dialog"
                        aria-current={active ? 'true' : undefined}
                        className={`flex items-center gap-2 w-full text-left px-3 py-2 rounded-lg border transition-colors cursor-pointer group ${active
                            ? 'bg-accent/5 border-accent-fg/60'
                            : 'bg-page border-line hover:border-accent-fg/40'
                            }`}
                    >
                        <span className="flex-1 min-w-0">
                            <span
                                className={`block text-xs font-semibold truncate ${active ? 'text-accent-fg' : 'text-fg-body group-hover:text-accent-fg'}`}
                                style={{ fontFamily: '"Inter", sans-serif' }}
                                title={ref.title}
                            >
                                {ref.title}
                            </span>
                            {ref.informationCategory && (
                                <span className="inline-block mt-1 px-1.5 py-px rounded bg-surface border border-line text-[10px] text-fg-2">
                                    {ref.informationCategory}
                                </span>
                            )}
                        </span>
                        <ChevronRightIcon
                            style={{ fontSize: 16 }}
                            className={active ? 'text-accent-fg' : 'text-fg-muted group-hover:text-accent-fg'}
                        />
                    </button>
                );
            })}
            {references.length > REFERENCES_SHOWN && (
                <button
                    type="button"
                    onClick={() => setShowAll(v => !v)}
                    className="px-0 py-0.5 border-none bg-transparent text-[11px] text-fg-2 hover:text-accent-fg cursor-pointer"
                >
                    {showAll ? 'Show fewer' : `Show ${hidden} more`}
                </button>
            )}
        </div>
    );
};

export const generateInteractionId = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

const NARROW_WIDTH = '24rem';
// Wide enough that an answer's tables stop scrolling sideways, capped so the page's
// own column keeps at least half the row.
const EXPANDED_WIDTH = 'min(45rem, 50%)';

const WIDTH_TRANSITION = '225ms cubic-bezier(0.4, 0, 0.6, 1)';

/**
 * A docked chat column's width, or none while the page's drawer is widened and needs
 * the room. The column is emptied rather than unmounted, so its conversation is still
 * there when the drawer narrows. Hiding waits out the narrowing, and keeps the empty
 * column's controls out of the tab order.
 */
export const chatColumnStyle = (width: string, yielded: boolean): React.CSSProperties => ({
    width: yielded ? 0 : width,
    overflow: 'hidden',
    visibility: yielded ? 'hidden' : 'visible',
    transition: `width ${WIDTH_TRANSITION}, visibility 0s ${yielded ? '225ms' : '0s'}`,
});

/**
 * A docked chat panel's conversation and width, for a page that hosts it in a row
 * beside a docked insight drawer.
 *
 * The conversation is one interactionId, so the agent sees the earlier turns, and a
 * suggested follow-up reaches it with the answer that suggested it. "New conversation"
 * swaps the id; the page keys the panel on it so the thread clears with it.
 *
 * The panel narrows while the drawer is open: at the wide width the two would leave the
 * page's own column almost no room. The choice to widen is kept, so the panel widens
 * again once the drawer closes. While the drawer itself is widened, the panel gives
 * up its column entirely.
 */
/** Where a page that lets the panel be hidden remembers it, per viewer. */
const HIDDEN_KEY_PREFIX = 'chat_panel_hidden:';

const readHidden = (key?: string): boolean => {
    if (!key) return false;
    try {
        return localStorage.getItem(HIDDEN_KEY_PREFIX + key) === 'true';
    } catch {
        return false;
    }
};

const writeHidden = (key: string, hidden: boolean) => {
    try {
        if (hidden) localStorage.setItem(HIDDEN_KEY_PREFIX + key, 'true');
        else localStorage.removeItem(HIDDEN_KEY_PREFIX + key);
    } catch {
        // Private windows and blocked storage: the panel still hides for this visit.
    }
};

export const useChatPanel = (drawerOpen: boolean, drawerWide = false, options: { hideKey?: string } = {}) => {
    const { hideKey } = options;
    const [interactionId, setInteractionId] = useState(generateInteractionId);
    const [wantsWide, setWantsWide] = useState(false);
    // Hidden by the reader, as opposed to yielded to a wide drawer: emptied the same
    // way, so the conversation is still there when it comes back. Remembered per
    // page (hideKey), since a reader who hides it once wants the room every visit.
    const [hidden, setHidden] = useState(() => readHidden(hideKey));
    const newConversation = useCallback(() => setInteractionId(generateInteractionId()), []);
    const toggleExpand = useCallback(() => setWantsWide(prev => !prev), []);
    const setVisibility = useCallback((next: boolean) => {
        setHidden(next);
        if (hideKey) writeHidden(hideKey, next);
    }, [hideKey]);
    const hide = useCallback(() => setVisibility(true), [setVisibility]);
    const show = useCallback(() => setVisibility(false), [setVisibility]);
    const expanded = wantsWide && !drawerOpen;

    return {
        interactionId,
        expanded,
        yielded: drawerWide,
        hidden,
        show,
        newConversation,
        style: chatColumnStyle(expanded ? EXPANDED_WIDTH : NARROW_WIDTH, drawerWide || hidden),
        chatProps: {
            interactionId,
            onNewConversation: newConversation,
            expanded,
            onToggleExpand: toggleExpand,
            expandUnavailable: drawerOpen ? 'Close the side panel to widen the assistant' : undefined,
            onHide: hideKey ? hide : undefined,
        },
    };
};

/**
 * What stands in for a hidden assistant: a narrow tab on the edge the assistant
 * occupies (`side`) that brings it back. Vertical text keeps it to a sliver of width.
 */
export const ChatShowRail: React.FC<{ title: string; onShow: () => void; side?: 'left' | 'right' }> = ({ title, onShow, side = 'right' }) => (
    <button
        type="button"
        onClick={onShow}
        aria-label={`Show ${title}`}
        title={`Show ${title}`}
        className="flex-none h-full w-9 flex flex-col items-center gap-2 pt-4 border-0 bg-page text-fg-2 hover:text-accent-fg hover:bg-surface transition-colors cursor-pointer"
        style={side === 'left' ? { borderRight: `1px solid ${dsVar('line')}` } : { borderLeft: `1px solid ${dsVar('line')}` }}
    >
        <ForumOutlinedIcon style={{ fontSize: 18 }} />
        <span className="text-[11px] font-mono tracking-wider" style={{ writingMode: 'vertical-rl' }}>Assistant</span>
    </button>
);

const HeaderButton: React.FC<{
    label: string;
    onClick?: () => void;
    disabled?: boolean;
    children: React.ReactNode;
}> = ({ label, onClick, disabled, children }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={label}
        title={label}
        className="p-1.5 rounded-md border-none bg-transparent text-fg-2 hover:text-accent-fg hover:bg-accent/10 transition-colors cursor-pointer flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-fg-2 disabled:hover:bg-transparent"
    >
        {children}
    </button>
);

const MarkdownComponents = {
    p: ({ children }: any) => <p className="mb-3 last:mb-0 leading-relaxed text-xs" style={{ fontFamily: '"Inter", sans-serif' }}>{children}</p>,
    ul: ({ children }: any) => <ul className="list-disc ml-4 mb-3 space-y-1 text-xs" style={{ fontFamily: '"Inter", sans-serif' }}>{children}</ul>,
    ol: ({ children }: any) => <ol className="list-decimal ml-4 mb-3 space-y-1 text-xs" style={{ fontFamily: '"Inter", sans-serif' }}>{children}</ol>,
    li: ({ children }: any) => <li className="leading-relaxed" style={{ fontFamily: '"Inter", sans-serif' }}>{children}</li>,
    strong: ({ children }: any) => <strong className="font-bold text-accent-fg" style={{ fontFamily: '"Inter", sans-serif' }}>{children}</strong>,
    code: ({ children }: any) => <code className="bg-surface px-1.5 py-0.5 rounded text-accent-fg font-mono text-[10px] border border-line">{children}</code>,
    h1: ({ children }: any) => <h1 className="text-sm font-bold text-fg mb-2" style={{ fontFamily: '"JetBrains Mono", monospace' }}>{children}</h1>,
    h2: ({ children }: any) => <h2 className="text-xs font-bold text-fg mb-2" style={{ fontFamily: '"JetBrains Mono", monospace' }}>{children}</h2>,
    h3: ({ children }: any) => <h3 className="text-[11px] font-bold text-fg mb-1" style={{ fontFamily: '"JetBrains Mono", monospace' }}>{children}</h3>,
    table: ({ children }: any) => <div className="overflow-x-auto mb-3"><table className="w-full text-xs border-collapse border border-line" style={{ fontFamily: '"Inter", sans-serif' }}>{children}</table></div>,
    thead: ({ children }: any) => <thead className="bg-page">{children}</thead>,
    tbody: ({ children }: any) => <tbody>{children}</tbody>,
    tr: ({ children }: any) => <tr className="border-b border-line">{children}</tr>,
    th: ({ children }: any) => <th className="text-left px-3 py-2 text-[11px] font-bold text-accent-fg border border-line whitespace-nowrap" style={{ fontFamily: '"JetBrains Mono", monospace' }}>{children}</th>,
    td: ({ children }: any) => <td className="px-3 py-2 text-xs text-fg-soft border border-line" style={{ fontFamily: '"Inter", sans-serif' }}>{children}</td>,
};

const LoadingDots = () => (
    <div className="flex gap-2 items-center">
        <div className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full animate-bounce [animation-delay:-0.3s]"></div>
        <div className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full animate-bounce [animation-delay:-0.15s]"></div>
        <div className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full animate-bounce"></div>
    </div>
);

/**
 * The agent's live reasoning while an answer is pending, styled as provisional so it is
 * not read as the answer itself: a dashed version of the answer's amber rule that turns
 * solid when the answer replaces it, and the flow editor's run-panel signals for "live"
 * (pulsing gold dot, blinking caret on the streaming line).
 *
 * Older lines stay at #94A3B8 (~6.2:1 on this navy); #64748B would drop below AA.
 */
const ThinkingIndicator: React.FC<{ entries: ReasoningLine[]; interrupted: boolean }> = ({ entries, interrupted }) => {
    // With nothing streamed yet, a dropped stream costs nothing worth announcing:
    // the answer is still on its way over HTTP.
    const showInterrupted = interrupted && entries.length > 0;
    return (
        <div className="px-4 py-3 border-l-[3px] border-dashed border-[#F59E0B]/40">
            <div className="flex items-center gap-2 mb-2">
                <span
                    className={`w-2 h-2 rounded-full ${showInterrupted ? 'bg-fg-2' : 'bg-accent animate-dot-pulse'}`}
                    aria-hidden="true"
                />
                <span
                    className={`text-[11px] font-semibold tracking-wide ${showInterrupted ? 'text-fg-2' : 'text-accent-fg'}`}
                    style={{ fontFamily: '"Inter", sans-serif' }}
                    role="status"
                >
                    {showInterrupted ? 'Thinking interrupted' : 'Thinking'}
                </span>
            </div>

            {entries.length > 0 && (
                <div className="space-y-2">
                    {entries.map((entry, index) => {
                        const isCurrent = index === entries.length - 1 && !interrupted;
                        return (
                            <div key={index}>
                                {entry.activityTemplateName && (
                                    <p className="m-0 font-mono text-[10px] text-accent-fg/70">{entry.activityTemplateName}</p>
                                )}
                                <p
                                    className={`m-0 text-[11px] leading-relaxed ${isCurrent ? 'text-fg-soft' : 'text-fg-2'}`}
                                    style={{ fontFamily: '"Inter", sans-serif' }}
                                >
                                    {entry.reasoning}
                                    {isCurrent && (
                                        <span
                                            className="inline-block w-[6px] h-[11px] ml-[3px] align-[-1px] bg-accent animate-caret-blink"
                                            aria-hidden="true"
                                        />
                                    )}
                                </p>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* The pulse and caret say "working" once lines arrive; the dots cover the
                gap before the first one, and a stream that has gone quiet. */}
            {(entries.length === 0 || interrupted) && (
                <div className={entries.length > 0 ? 'mt-3' : ''}>
                    <LoadingDots />
                </div>
            )}
        </div>
    );
};

const ChatAssistance: React.FC<ChatAssistanceProps> = ({
    agentCode = "QUERY_DATA_INSIGHTS_AND_RESPOND",
    title,
    subtitle,
    initialMessage = "Hello! I'm your Data[S]cient assistant. I can help you deep-dive into these insights, find correlations, or summarize key findings. What would you like to explore?",
    contextData = {},
    interactionId,
    placeholder = "Ask assistant...",
    showReasoning = false,
    onOpenReference,
    activeReferenceId,
    onNewConversation,
    expanded = false,
    onToggleExpand,
    expandUnavailable,
    onHide,
}) => {
    const [message, setMessage] = useState('');
    const [messages, setMessages] = useState<{
        role: 'user' | 'assistant';
        text?: string;
        followupQuestions?: string[];
        sourceLinks?: string[];
        references?: AgentReference[];
        isLoading?: boolean;
    }[]>([
        {
            role: 'assistant',
            text: initialMessage
        }
    ]);
    const [isChatLoading, setIsChatLoading] = useState(false);
    const messagesEndRef = React.useRef<HTMLDivElement>(null);
    const reasoning = useReasoningStream();

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, reasoning.entries]);

    const handleSend = async (textToSend: string = message) => {
        if (!textToSend.trim()) return;

        const newUserMessage = { role: 'user' as const, text: textToSend };
        const newAssistantLoadingMessage = { role: 'assistant' as const, isLoading: true };

        setMessages(prev => [...prev, newUserMessage, newAssistantLoadingMessage]);
        setMessage('');
        setIsChatLoading(true);

        const newInteractionId = interactionId ?? generateInteractionId();
        // Sent only once the stream has its snapshot, so this request's reasoning can be
        // told apart from earlier answers' under the same interactionId.
        const reasoningToken = showReasoning ? await reasoning.start(newInteractionId) : null;

        const payload = {
            data: {
                query: textToSend,
                ...contextData
            },
            user: {
                interactionId: newInteractionId
            },
            agent: {
                code: agentCode
            }
        };

        try {
            const response = await axios.post(`${environment.controlplaneUrl}/agent/flow/execute`, payload, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });

            let responseText = '';
            let followupQuestions: string[] = [];
            let sourceLinks: string[] = [];
            const references: AgentReference[] = [];

            if (response.data && Array.isArray(response.data.data)) {
                response.data.data.forEach((item: any) => {
                    if (item.objectType === 'PARAGRAPH' && item.key === 'response') {
                        responseText = item.value;
                    } else if (item.objectType === 'MULTITEXTOPTIONS' && item.key === 'followupQuestions') {
                        followupQuestions = item.value || [];
                    } else if (item.objectType === 'MULTITEXTOPTIONS' && item.key === 'sourceLinks') {
                        sourceLinks = item.value || [];
                    } else {
                        references.push(...referencesOf(item));
                    }
                });
            }

            setMessages(prev => {
                const newMessages = [...prev];
                newMessages[newMessages.length - 1] = {
                    role: 'assistant',
                    text: responseText || 'No response provided.',
                    followupQuestions,
                    sourceLinks,
                    references
                };
                return newMessages;
            });

        } catch (error) {
            console.error("Failed to execute chat flow:", error);
            setMessages(prev => {
                const newMessages = [...prev];
                newMessages[newMessages.length - 1] = {
                    role: 'assistant',
                    text: 'Sorry, I encountered an error while processing your request.'
                };
                return newMessages;
            });
        } finally {
            if (reasoningToken !== null) reasoning.stop(reasoningToken);
            setIsChatLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSend();
        }
    };

    return (
        // Sets its own text colour: markdown paragraphs and list items inherit it, and a
        // host that sets none would leave them black on navy.
        <div className="flex flex-col h-full bg-surface text-fg-body overflow-hidden">

            {/*
              * Only drawn when the caller names the panel. Some call sites sit
              * under a label of their own and pass no title; they keep the
              * headerless window they have always had.
              */}
            {title && (
                <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-line bg-page/50">
                    <div className="min-w-0">
                        <p className="m-0 text-xs font-bold text-fg" style={{ fontFamily: '"JetBrains Mono", monospace' }}>
                            {title}
                        </p>
                        {subtitle && (
                            <p className="m-0 mt-0.5 text-[10px] text-fg-muted" style={{ fontFamily: '"Inter", sans-serif' }}>
                                {subtitle}
                            </p>
                        )}
                    </div>
                    {(onNewConversation || onToggleExpand || onHide) && (
                        <div className="flex items-center gap-0.5 -mr-1.5">
                            {onNewConversation && (
                                // Nothing to start over from while only the greeting shows.
                                <HeaderButton label="New conversation" onClick={onNewConversation} disabled={messages.length <= 1}>
                                    <RestartAltIcon style={{ fontSize: 16 }} />
                                </HeaderButton>
                            )}
                            {onToggleExpand && (
                                <HeaderButton
                                    label={expandUnavailable ?? (expanded ? 'Narrow assistant' : 'Widen assistant')}
                                    onClick={onToggleExpand}
                                    disabled={Boolean(expandUnavailable)}
                                >
                                    {expanded
                                        ? <CloseFullscreenIcon style={{ fontSize: 14 }} />
                                        : <OpenInFullIcon style={{ fontSize: 14 }} />}
                                </HeaderButton>
                            )}
                            {onHide && (
                                <HeaderButton label="Hide assistant" onClick={onHide}>
                                    <KeyboardDoubleArrowRightIcon style={{ fontSize: 16 }} />
                                </HeaderButton>
                            )}
                        </div>
                    )}
                </div>
            )}

            <div className="flex-1 overflow-y-auto custom-scrollbar">
                {messages.map((msg, index) => (
                    <div key={index} className={`w-full ${msg.role === 'user' ? 'bg-raised border-l-[3px] border-l-[#60A5FA] border-y border-y-line' : 'bg-page'}`}>
                        {msg.isLoading ? (
                            showReasoning ? (
                                // Only the last loading message is in flight; earlier ones are settled.
                                <ThinkingIndicator
                                    entries={index === messages.length - 1 ? reasoning.entries : []}
                                    interrupted={index === messages.length - 1 && reasoning.interrupted}
                                />
                            ) : (
                                <div className="px-4 py-4 border-l-[3px] border-[#F59E0B]">
                                    <LoadingDots />
                                </div>
                            )
                        ) : (
                            <div className={`px-4 py-2.5 ${msg.role === 'assistant' ? 'border-l-[3px] border-[#F59E0B]' : ''}`}>
                                {msg.role === 'user' ? (
                                    <p className="text-xs leading-relaxed text-fg-soft" style={{ fontFamily: '"Inter", sans-serif' }}>
                                        {msg.text}
                                    </p>
                                ) : (
                                    <ReactMarkdown 
                                        remarkPlugins={[remarkGfm]}
                                        components={MarkdownComponents}
                                    >
                                        {msg.text || ''}
                                    </ReactMarkdown>
                                )}

                                {onOpenReference && msg.references && msg.references.length > 0 && (
                                    <ReferenceList
                                        references={msg.references}
                                        activeReferenceId={activeReferenceId}
                                        onOpen={onOpenReference}
                                    />
                                )}

                                {msg.followupQuestions && msg.followupQuestions.length > 0 && (
                                    <div className="mt-2.5 space-y-1.5">
                                        {msg.followupQuestions.map((q, qIndex) => (
                                            <button
                                                key={qIndex}
                                                onClick={() => handleSend(q)}
                                                className="block w-full text-left px-3 py-2 rounded-lg bg-page border border-line hover:border-accent-fg/40 transition-colors text-xs text-fg-2 hover:text-accent-fg cursor-pointer"
                                            >
                                                {q}
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {msg.sourceLinks && msg.sourceLinks.length > 0 && (
                                    <div className="mt-3 space-y-1.5">
                                        <p className="text-[10px] text-fg-muted font-bold uppercase tracking-wider mb-1">Source Links</p>
                                        {msg.sourceLinks.map((link, lIndex) => (
                                            <a
                                                key={lIndex}
                                                href={link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="block w-full text-left px-3 py-2 rounded-lg bg-page border border-line hover:border-[#4285F4]/50 transition-colors text-xs text-[#4285F4] hover:text-fg cursor-pointer overflow-hidden text-ellipsis whitespace-nowrap"
                                            >
                                                {link}
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                ))}
                <div ref={messagesEndRef} />
            </div>

            <div className="px-3 py-2.5 border-t border-line bg-page/50">
                <div className="relative">
                    <input
                        type="text"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder={placeholder}
                        disabled={isChatLoading}
                        className="w-full bg-surface border border-line rounded-xl py-2.5 pl-4 pr-12 text-xs text-fg placeholder:text-fg-muted outline-none focus:border-accent-fg/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                    <button
                        onClick={() => handleSend()}
                        disabled={isChatLoading || !message.trim()}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-accent-fg hover:bg-accent/10 rounded-lg transition-colors border-none bg-transparent cursor-pointer flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <SendIcon style={{ fontSize: 16 }} />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ChatAssistance;
